import type { DatasetStructureChannel } from "@/api";

/**
 * 운동 보기의 **표시용** 팔 귀속.
 *
 * 이 판정은 카드 정렬(Left → Right → 공통)에만 쓴다. 채널 제목·그룹 분류는 이 모듈을 쓰지
 * 않는다. `parquetChannels.ts` 가 그 둘을 소유한다.
 */

export type ArmSide = "left" | "right";

/** 채널 이름을 `_`·`-` 로 나눴을 때 방향을 뜻하는 토큰. */
export const DIRECTION_TOKEN = /^(left|right)$/i;

/**
 * 채널이 어느 팔에 속하는지 돌려준다. `left` | `right` | `null`(어느 팔에도 속하지 않음).
 *
 * `arm_side` 가 SoT 다. 그 값이 오면 그대로 쓴다. 그러나 서버의 방향 도출(hatchery-data
 * `schemas/_channel.py` 의 `split_arm_side`)은 접두(`left_…`)와 접미(`…_left`)만 본다. 그래서
 * 방향이 이름 **중간**에 있는 `arm_left_ee_pose` 는 `arm_side` 가 null 로 온다. 속력·가속도를
 * 담는 팔 채널은 모두 이 경우에 해당한다(ZERG-643). 그래서 `arm_side` 가 없을 때만 이름의
 * 방향 토큰으로 보조 판정한다.
 *
 * 이 판정은 `parquetChannels.ts` 의 "팔 방향을 이름에서 추론하지 않는다" 규칙의 **표시 전용
 * 예외**다. ZERG-643 이 해결되면 이 폴백을 제거한다.
 *
 * 양쪽 토큰이 모두 있으면(`arm_left_to_right_ee_pose`) 두 팔 사이의 상대량이다. 그래서 어느
 * 팔에도 넣지 않는다. 단순한 `_left_` 포함 검사는 이 채널을 Left 로 잘못 판정한다. 판정할 수
 * 없으면 좌우로 나누지 않는다. 잘못된 팔에 넣는 것보다 공통에 두는 편이 오류가 적다.
 */
export function displayArmSide(
  channel: DatasetStructureChannel | undefined,
  key: string,
): ArmSide | null {
  if (channel?.arm_side === "left" || channel?.arm_side === "right") {
    return channel.arm_side;
  }
  let left = false;
  let right = false;
  for (const token of (channel?.name ?? keyName(key)).split(/[_-]/)) {
    if (!DIRECTION_TOKEN.test(token)) {
      continue;
    }
    if (token.toLowerCase() === "left") {
      left = true;
    } else {
      right = true;
    }
  }
  // 둘 다면 상대량이다. 둘 다 아니면 방향이 없는 채널이다. 두 경우 모두 한 팔의 값이 아니다.
  if (left === right) {
    return null;
  }
  return left ? "left" : "right";
}

/** 진단 그리드 키(`<topic>::<name>`)의 name 부분. 구분자가 없으면 키 전체. */
function keyName(key: string): string {
  const index = key.indexOf("::");
  return index === -1 ? key : key.slice(index + 2);
}
